import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://fiifit.ro';

export interface Disease {
  id: string;
  code: string;
  name: string;
  category: string;
  shortDescription: string;
  symptoms: string[];
  causes?: string[];
  relatedDiseaseIds?: string[];
  foodsAllowed?: string[];
  foodsToAvoid?: string[];
  nutritionNotes?: string[];
  recommendedExercises?: string[];
  exercisesToAvoid?: string[];
  lifestyleTips?: string[];
  createdAt: string;
}

export interface DiseaseCategory {
  category: string;
  count: number;
  label: string;
}

export interface UserDiseases {
  primary: Disease | null;
  secondary: Disease[];
  profile: {
    primaryCondition: string | null;
    secondaryConditions: any;
  };
}

class DiseasesService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      Authorization: token ? `Bearer ${token}` : '',
    };
  }

  /**
   * Get all diseases with optional filters
   */
  async getAllDiseases(filters?: {
    category?: string;
    search?: string;
  }): Promise<Disease[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.category) params.append('category', filters.category);
      if (filters?.search) params.append('search', filters.search);

      const response = await axios.get(
        `${API_URL}/api/diseases?${params.toString()}`
      );

      return response.data.data;
    } catch (error) {
      console.error('Error fetching diseases:', error);
      throw error;
    }
  }

  /**
   * Get disease categories with counts
   */
  async getCategories(): Promise<DiseaseCategory[]> {
    try {
      const response = await axios.get(`${API_URL}/api/diseases/categories`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  /**
   * Get disease by code
   */
  async getDiseaseByCode(code: string): Promise<Disease> {
    try {
      const response = await axios.get(`${API_URL}/api/diseases/${code}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching disease:', error);
      throw error;
    }
  }

  /**
   * Get related diseases for a disease
   */
  async getRelatedDiseases(code: string): Promise<Disease[]> {
    try {
      const response = await axios.get(
        `${API_URL}/api/diseases/${code}/related`
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching related diseases:', error);
      throw error;
    }
  }

  /**
   * Set user's primary disease (requires auth)
   */
  async setUserPrimaryDisease(diseaseCode: string): Promise<any> {
    try {
      const response = await axios.post(
        `${API_URL}/api/diseases/user/primary`,
        { diseaseCode },
        { headers: this.getAuthHeaders() }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error setting primary disease:', error);
      throw error;
    }
  }

  /**
   * Add secondary disease for user (requires auth)
   */
  async addUserSecondaryDisease(diseaseCode: string): Promise<any> {
    try {
      const response = await axios.post(
        `${API_URL}/api/diseases/user/secondary`,
        { diseaseCode },
        { headers: this.getAuthHeaders() }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error adding secondary disease:', error);
      throw error;
    }
  }

  /**
   * Remove secondary disease for user (requires auth)
   */
  async removeUserSecondaryDisease(diseaseCode: string): Promise<void> {
    try {
      await axios.delete(
        `${API_URL}/api/diseases/user/secondary/${diseaseCode}`,
        { headers: this.getAuthHeaders() }
      );
    } catch (error) {
      console.error('Error removing secondary disease:', error);
      throw error;
    }
  }

  /**
   * Get user's diseases (primary + secondary) with full details (requires auth)
   */
  async getUserDiseases(): Promise<UserDiseases> {
    try {
      const response = await axios.get(
        `${API_URL}/api/diseases/user/my-diseases`,
        { headers: this.getAuthHeaders() }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching user diseases:', error);
      throw error;
    }
  }

  /**
   * Search diseases by name
   */
  async searchDiseases(query: string): Promise<Disease[]> {
    return this.getAllDiseases({ search: query });
  }

  /**
   * Get diseases by category
   */
  async getDiseasesByCategory(category: string): Promise<Disease[]> {
    return this.getAllDiseases({ category });
  }
}

export const diseasesService = new DiseasesService();
export default diseasesService;
